/**
 * Lenis Smooth Scroll Module
 *
 * Integrates Lenis with GSAP ScrollTrigger for smooth scrolling
 * and proper animation coordination.
 *
 * Usage:
 * - Add data-lenis-prevent to elements that should have native scroll (modals, etc.)
 * - Use window.lenis.scrollTo(target) for smooth anchor navigation
 * - Use window.lenis.stop() and window.lenis.start() to control scroll
 */

// Store lenis instance globally for access from other modules
let lenisInstance = null;

export function getLenis() {
    return lenisInstance;
}

export default function ScrollSmooth() {
    // Only enable on desktop - touch devices have native smooth scroll
    const isDesktop = window.matchMedia('(min-width: 1090px)').matches;

    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!isDesktop || prefersReducedMotion) {
        console.log('[Lenis] Disabled: Desktop:', isDesktop, '| Reduced Motion:', prefersReducedMotion);
        return null;
    }

    // Pages that own the scroll (snap, custom raf) must run their Lenis solo.
    // Global Lenis here would double-bind wheel/touch listeners and fight the snap.
    if (document.getElementById('product-detail-desk')) {
        console.log('[Lenis] Skipped: product-detail-desk runs its own snap-scroll Lenis');
        return null;
    }

    if (document.getElementById('talk-to-us')) {
        console.log('[Lenis] Skipped: talk-to-us runs its own snap-scroll Lenis');
        return null;
    }

    if (document.getElementById('blog-listing-snap')) {
        console.log('[Lenis] Skipped: blog-listing-snap runs its own snap-scroll Lenis');
        return null;
    }

    if (document.getElementById('esg-hero')) {
        console.log('[Lenis] Skipped: esg page runs its own snap-scroll Lenis');
        return null;
    }

    try {
        // Initialize Lenis with optimized settings
        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential easing
            orientation: 'vertical', // Fixed typo from 'oientation'
            gestureOrientation: 'vertical',
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
            infinite: false,
            autoResize: true,
        });

        // Store globally for access from other modules (anchor links, etc.)
        window.lenis = lenis;
        lenisInstance = lenis;

        // Integrate with GSAP ScrollTrigger if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            // Sync Lenis scroll with GSAP ScrollTrigger
            lenis.on('scroll', ScrollTrigger.update);

            // Use GSAP ticker for smoother animation synchronization
            gsap.ticker.add((time) => {
                lenis.raf(time * 1000);
            });

            // Disable GSAP's internal lag smoothing to prevent conflicts
            gsap.ticker.lagSmoothing(0);

            console.log('[Lenis] Integrated with GSAP ScrollTrigger');
        } else {
            // Fallback: Use requestAnimationFrame if GSAP is not available
            function raf(time) {
                lenis.raf(time);
                requestAnimationFrame(raf);
            }
            requestAnimationFrame(raf);

            console.log('[Lenis] Running standalone (no GSAP)');
        }

        // Handle anchor links with smooth scroll
        setupAnchorLinks(lenis);

        // Handle resize events
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Check if we should disable Lenis on resize (e.g., mobile)
                const shouldBeEnabled = window.matchMedia('(min-width: 1090px)').matches;
                if (!shouldBeEnabled && lenis) {
                    lenis.destroy();
                    window.lenis = null;
                    lenisInstance = null;
                    console.log('[Lenis] Destroyed on resize to mobile');
                }
            }, 250);
        });

        console.log('[Lenis] Initialized successfully');
        return lenis;

    } catch (error) {
        console.error('[Lenis] Failed to initialize:', error);
        return null;
    }
}

/**
 * Setup anchor links to use Lenis smooth scroll
 */
function setupAnchorLinks(lenis) {
    document.addEventListener('click', (e) => {
        const anchor = e.target.closest('a[href^="#"]');
        if (!anchor) return;

        const href = anchor.getAttribute('href');
        if (!href || href === '#') return;

        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();

        // Use Lenis scrollTo for smooth scrolling
        lenis.scrollTo(target, {
            offset: 0,
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });
    });
}
